import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Bell, Check, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/components/Cards";
import { useI18n } from "@/context/I18nContext";
import { useNavigate } from "react-router-dom";

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useI18n();
  const navigate = useNavigate();
  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
        await api.post("/notifications/mark-read");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const linkFor = (n) => {
    if (n.target_type === "report") return `/reports/${n.target_id}`;
    if (n.target_type === "post") return `/posts/${n.target_id}`;
    return "#";
  };

  return (
    <MobileShell
      topBar={
        <div className="h-14 flex items-center gap-2 px-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="notif-back-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="font-extrabold text-[20px] text-[var(--te-text)]">
            {L("الإشعارات", "התראות", "Notifications")}
          </h1>
        </div>
      }
    >
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
      ) : items.length === 0 ? (
        <div className="px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--te-primary-soft)] flex items-center justify-center mb-3">
            <Bell size={26} className="text-[var(--te-primary)]" />
          </div>
          <h3 className="font-extrabold text-[16px] text-[var(--te-text)]">
            {L("لا توجد إشعارات", "אין התראות", "All caught up")}
          </h3>
          <p className="text-[13px] text-[var(--te-text-muted)] mt-1.5">
            {L("سنُعلمك حال وصول أي تحديث.", "נעדכן אותך כשיהיה משהו חדש.", "We'll let you know when something new happens.")}
          </p>
        </div>
      ) : (
        <div className="px-4 py-3 space-y-2">
          {items.map((n) => (
            <Link
              to={linkFor(n)}
              key={n.id}
              data-testid={`notification-${n.id}`}
              className={`block te-card te-card-hover p-3.5 te-fade-up ${!n.read ? "!bg-[var(--te-primary-soft)] !border-[var(--te-primary)]/30" : ""}`}
            >
              <div className="flex items-center gap-3">
                <div className="te-icon-tile shrink-0">
                  <Bell size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-[var(--te-text)] line-clamp-2" dir="auto">{n.message}</div>
                  <div className="text-[11px] text-[var(--te-text-muted)] mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
                {n.read && <Check size={16} className="text-[var(--te-text-muted)] shrink-0" />}
                {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--te-primary)] shrink-0" />}
              </div>
            </Link>
          ))}
        </div>
      )}
    </MobileShell>
  );
}
